import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Users, CalendarDays, FileText, Sparkles, Inbox, Phone, Mail, Package, ExternalLink, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import api, { API } from "@/lib/api";
import { LOGO } from "@/data/site";
import CollectionsAdmin from "@/components/admin/CollectionsAdmin";

const STATUSES = ["new", "contacted", "site_visit_booked", "quote_sent", "quote_approved", "materials_selected", "installation_booked", "in_progress", "completed"];
const KIND_LABEL = { booking: "Booking", quote: "Quote", contact: "Contact", handyman: "Handyman", ai_design: "AI Design" };
const KIND_FILTERS = ["all", "ai_design", "booking", "quote", "handyman", "contact"];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [catProducts, setCatProducts] = useState([]);
  const emptyCat = { category_key: "Toilet", category_label: "Toilets", name: "", price_gbp: "", unit: "", swap_prompt: "", supplier: "Victoria Plumbing", sku: "", supplier_url: "", surface: false, image_base64: "" };
  const [catForm, setCatForm] = useState(emptyCat);
  const [savingCat, setSavingCat] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login");
  }, [user, loading, navigate]);

  const load = () => {
    setBusy(true);
    Promise.all([api.get("/admin/stats"), api.get("/admin/enquiries"), api.get("/admin/designs"), api.get("/admin/studio-products")])
      .then(([s, e, d, c]) => { setStats(s.data); setEnquiries(e.data || []); setDesigns(d.data || []); setCatProducts(c.data || []); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}/status`, { status });
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) { /* noop */ }
  };

  const CAT_OPTIONS = [
    ["Toilet", "Toilets"], ["Basin", "Basins"], ["Bath", "Baths"], ["Shower", "Showers"],
    ["Vanity", "Vanity Units"], ["Taps", "Taps"], ["Radiator", "Towel Rails"], ["Mirror", "Mirrors"],
    ["Tiles", "Tiles"], ["Splashback", "Splashbacks"], ["KitchenUnits", "Kitchen Units"], ["Worktops", "Worktops"],
  ];

  const onCatFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCatForm((f) => ({ ...f, image_base64: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const addProduct = async () => {
    if (!catForm.name || !catForm.swap_prompt) return;
    setSavingCat(true);
    try {
      const payload = { ...catForm, price_gbp: parseFloat(catForm.price_gbp) || 0 };
      const { data } = await api.post("/admin/studio-products", payload);
      setCatProducts((p) => [data, ...p]);
      setCatForm(emptyCat);
    } catch (e) { /* noop */ }
    finally { setSavingCat(false); }
  };

  const deleteProduct = async (id) => {
    try { await api.delete(`/admin/studio-products/${id}`); setCatProducts((p) => p.filter((x) => x.id !== id)); } catch (e) { /* noop */ }
  };

  if (loading || !user) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-rossi-gold" /></div>;

  const filtered = filter === "all" ? enquiries : enquiries.filter((e) => e.kind === filter);
  const cards = stats ? [
    ["New leads", stats.new_enquiries, Inbox], ["Total enquiries", stats.total_enquiries, Users],
    ["Bookings", stats.bookings, CalendarDays], ["Quotes", stats.quotes, FileText], ["AI designs", stats.designs, Sparkles],
  ] : [];

  return (
    <div data-testid="admin-dashboard-page" className="container-rossi py-10">
      <Seo title="Admin Dashboard" description="Rossi Ceramics CRM dashboard." />
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Rossi Ceramics" className="h-9 w-9 object-contain" />
          <div>
            <h1 className="font-serif text-2xl text-rossi-cream">Rossi CRM Dashboard</h1>
            <p className="text-xs text-rossi-stone">Welcome back, {user.name}</p>
          </div>
        </div>
        <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost-gold !py-2" data-testid="admin-logout"><LogOut size={15} /> Sign out</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        {cards.map(([label, value, Icon]) => (
          <div key={label} className="card-lux p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <Icon size={20} className="text-rossi-gold mb-3" />
            <p className="font-serif text-3xl text-rossi-cream">{value ?? "–"}</p>
            <p className="text-xs text-rossi-stone mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {KIND_FILTERS.map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`} className={`px-4 py-2 rounded-full text-sm ${filter === k ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80 hover:bg-rossi-gold/10"}`}>{k === "all" ? "All" : KIND_LABEL[k]}</button>
        ))}
      </div>

      {busy ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-rossi-gold" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-rossi-stone text-center py-16">No enquiries yet.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => (
            <div key={e.id} className="card-lux p-5" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono uppercase tracking-widest text-rossi-gold">{KIND_LABEL[e.kind] || e.kind}</span>
                    <span className="text-rossi-cream font-medium">{e.name}</span>
                    <span className="text-xs text-rossi-stone">{new Date(e.created_at).toLocaleString("en-GB")}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-rossi-cream/75">
                    {e.email && <a href={`mailto:${e.email}`} className="flex items-center gap-1 hover:text-rossi-gold"><Mail size={13} /> {e.email}</a>}
                    {e.phone && <a href={`tel:${e.phone}`} className="flex items-center gap-1 hover:text-rossi-gold"><Phone size={13} /> {e.phone}</a>}
                    {e.postcode && <span>{e.postcode}</span>}
                  </div>
                  {e.message && <p className="text-sm text-rossi-cream/70 mt-2">{e.message}</p>}
                  {e.payload && Object.keys(e.payload).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {Object.entries(e.payload).map(([k, v]) => (
                        <span key={k} className="text-[11px] bg-rossi-bg border border-white/10 rounded-full px-2.5 py-1 text-rossi-cream/70">{k.replace(/_/g, " ")}: {Array.isArray(v) ? v.join(", ") : String(v)}</span>
                      ))}
                    </div>
                  )}
                </div>
                <select value={e.status} onChange={(ev) => updateStatus(e.id, ev.target.value)} className="bg-rossi-bg border border-rossi-gold/30 rounded-lg px-3 py-2 text-sm text-rossi-cream focus:outline-none" data-testid={`status-${e.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      <CollectionsAdmin />

      {/* CATALOGUE EDITOR */}
      <div className="mt-14" data-testid="catalogue-editor">
        <div className="flex items-center gap-2 mb-2">
          <Package size={20} className="text-rossi-gold" />
          <h2 className="font-serif text-2xl text-rossi-cream">Studio Catalogue</h2>
        </div>
        <p className="text-xs text-rossi-stone mb-6">Add your own products to The Studio — with image, price and a private supplier link (shown only on your order sheets, never to customers).</p>
        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          <div className="card-lux p-5">
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs text-rossi-stone">Category
                <select value={catForm.category_key} onChange={(e) => { const o = CAT_OPTIONS.find((x) => x[0] === e.target.value); setCatForm({ ...catForm, category_key: o[0], category_label: o[1] }); }} className="mt-1 w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" data-testid="cat-category">
                  {CAT_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <input placeholder="Product name" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className="col-span-2 bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" data-testid="cat-name" />
              <input placeholder="Price £" type="number" value={catForm.price_gbp} onChange={(e) => setCatForm({ ...catForm, price_gbp: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" data-testid="cat-price" />
              <input placeholder="Unit (e.g. /m²)" value={catForm.unit} onChange={(e) => setCatForm({ ...catForm, unit: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" />
              <input placeholder="AI instruction (e.g. change tap to gold)" value={catForm.swap_prompt} onChange={(e) => setCatForm({ ...catForm, swap_prompt: e.target.value })} className="col-span-2 bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" data-testid="cat-prompt" />
              <input placeholder="Supplier" value={catForm.supplier} onChange={(e) => setCatForm({ ...catForm, supplier: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" />
              <input placeholder="Supplier SKU" value={catForm.sku} onChange={(e) => setCatForm({ ...catForm, sku: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" />
              <input placeholder="Supplier link (https://…)" value={catForm.supplier_url} onChange={(e) => setCatForm({ ...catForm, supplier_url: e.target.value })} className="col-span-2 bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream" data-testid="cat-supplier-url" />
              <label className="col-span-2 flex items-center gap-2 text-xs text-rossi-cream/75"><input type="checkbox" checked={catForm.surface} onChange={(e) => setCatForm({ ...catForm, surface: e.target.checked })} className="accent-rossi-gold" /> Surface (tile/worktop — apply only, no drag)</label>
              <label className="col-span-2 text-xs text-rossi-stone">Product image
                <input type="file" accept="image/*" onChange={onCatFile} className="mt-1 w-full text-xs text-rossi-cream/70" data-testid="cat-image" />
              </label>
              {catForm.image_base64 && <img src={catForm.image_base64} alt="preview" className="col-span-2 h-24 w-24 object-contain bg-white/5 rounded-lg p-1" />}
            </div>
            <button onClick={addProduct} disabled={savingCat || !catForm.name || !catForm.swap_prompt} className="btn-gold w-full mt-4 disabled:opacity-40" data-testid="cat-add">{savingCat ? <Loader2 size={16} className="animate-spin" /> : "Add to catalogue"}</button>
          </div>
          <div className="space-y-2">
            {catProducts.length === 0 ? <p className="text-rossi-stone text-sm py-6">No custom products yet.</p> : catProducts.map((p) => (
              <div key={p.id} className="card-lux p-3 flex items-center gap-3" data-testid={`cat-product-${p.id}`}>
                {p.image ? <img src={`${API}${p.image.replace('/api', '')}`} alt={p.name} className="h-12 w-12 object-contain bg-white/5 rounded" /> : <div className="h-12 w-12 bg-white/5 rounded" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-rossi-cream truncate">{p.name} <span className="text-xs text-rossi-stone">· {p.category_label}</span></p>
                  <p className="text-xs text-rossi-gold font-mono">£{p.price_gbp}{p.unit || ""} · {p.supplier} {p.sku}</p>
                </div>
                <button onClick={() => deleteProduct(p.id)} className="text-rossi-stone hover:text-red-400" data-testid={`cat-delete-${p.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* OWNER ORDER SHEETS */}
      {designs.filter((d) => d.final_image_id && (d.kind !== "cad" || d.contact)).length > 0 && (
        <div className="mt-14" data-testid="order-sheets">
          <div className="flex items-center gap-2 mb-2">
            <Package size={20} className="text-rossi-gold" />
            <h2 className="font-serif text-2xl text-rossi-cream">Design Order Sheets</h2>
          </div>
          <p className="text-xs text-rossi-stone mb-6">Owner only — real supplier products &amp; links for each finalised Studio design. Not visible to customers.</p>
          <div className="space-y-6">
            {designs.filter((d) => d.final_image_id && (d.kind !== "cad" || d.contact)).map((d) => (
              <div key={d.id} className="card-lux p-5" data-testid={`order-sheet-${d.id}`}>
                <div className="grid md:grid-cols-[220px_1fr] gap-5">
                  <div>
                    <img src={`${API}/studio/image/${d.final_image_id}`} alt="Finalised design" className="rounded-lg w-full object-cover border border-rossi-gold/20" />
                    <p className="text-xs text-rossi-stone mt-2">Ref RC-{(d.id || "").slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
                      <span className="text-rossi-cream font-medium">{d.contact?.name || "—"}</span>
                      <span className="text-xs text-rossi-stone">{d.room_type} · {d.style}</span>
                      {d.contact?.email && <a href={`mailto:${d.contact.email}`} className="text-xs text-rossi-cream/75 hover:text-rossi-gold flex items-center gap-1"><Mail size={12} /> {d.contact.email}</a>}
                      {d.contact?.phone && <a href={`tel:${d.contact.phone}`} className="text-xs text-rossi-cream/75 hover:text-rossi-gold flex items-center gap-1"><Phone size={12} /> {d.contact.phone}</a>}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-rossi-stone border-b border-white/10">
                          <th className="py-2 pr-3">Product</th><th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Supplier</th><th className="py-2 pr-3">Ref</th><th className="py-2 pr-3">Est. £</th><th className="py-2">Order</th>
                        </tr></thead>
                        <tbody>
                          {(d.order || []).map((p, i) => (
                            <tr key={i} className="border-b border-white/5">
                              <td className="py-2 pr-3 text-rossi-cream">{p.name}</td>
                              <td className="py-2 pr-3 text-rossi-cream/70">{p.category}</td>
                              <td className="py-2 pr-3 text-rossi-cream/70">{p.supplier}</td>
                              <td className="py-2 pr-3 font-mono text-rossi-stone text-xs">{p.sku}</td>
                              <td className="py-2 pr-3 text-rossi-gold font-mono">£{Number(p.price_gbp || 0).toFixed(2)}{p.unit || ""}</td>
                              <td className="py-2">{p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="text-rossi-gold hover:underline flex items-center gap-1">Buy <ExternalLink size={11} /></a> : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {(() => {
                      const flat = (d.order || []).filter((p) => !p.unit);
                      const sub = flat.reduce((a, p) => a + (p.price_gbp || 0), 0);
                      const perM = (d.order || []).filter((p) => p.unit);
                      return (
                        <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-2 text-sm">
                          <span className="text-rossi-stone">Estimated subtotal <span className="text-xs">(fixtures &amp; fittings)</span></span>
                          <span className="font-mono text-rossi-gold text-base" data-testid={`order-subtotal-${d.id}`}>£{sub.toFixed(2)}</span>
                          {perM.length > 0 && <span className="w-full text-[11px] text-rossi-stone">+ {perM.length} item(s) priced per m² (tiles / worktops) — add once the area is measured.</span>}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
